/**
 * Direct Item Library Integration for Cut & Fills
 * Bypasses complex mapping and directly gets rates from item library
 */

import { ItemLibraryResponse, ItemLibraryItem } from "../api/itemLibrary";
import { ALL_REFERENCES } from "./cutFillsReferenceMapping";

/**
 * Correct units for each Cut & Fills field, matching what is shown in CutFills.tsx columns
 */
const FIELD_UNITS: Record<string, string> = {
  // Site Clearance
  vegStrip: 'm²',
  topsoilStrip: 'm³',
  topsoilDisposalOnsite: 'm³',
  topsoilDisposalOffsite: 'm³',
  // Cut & Fill
  cutFounds: 'm³',
  fillFounds: 'm³',
  cutPaths: 'm³',
  fillPaths: 'm³',
  cutGarden: 'm³',
  fillGarden: 'm³',
  cutDrives: 'm³',
  fillDrives: 'm³',
  fillToMakeUp6F2: 'm³',
};

export interface RateInfo {
  rate: number;
  description: string;
  unit: string;
  resourceCode?: string;
}

/**
 * Create a simple rate lookup map from item library for Cut & Fills
 * Maps field names to rate info (rate, description, unit)
 */
export function createCutFillsRateLookupMap(
  itemLibraryData: ItemLibraryResponse | undefined,
): Map<string, RateInfo> {
  const rateMap = new Map<string, RateInfo>();

  if (!itemLibraryData) {
    return rateMap;
  }

  // Create a map of resourceCode to item for quick lookup
  const resourceCodeMap = new Map<string, ItemLibraryItem>();
  itemLibraryData.forEach((item: ItemLibraryItem) => {
    const code = item.resourceCode?.trim().toUpperCase();
    if (code) {
      resourceCodeMap.set(code, item);
    }
  });

  // Map each field name to its rate info (rate, description, unit)
  Object.entries(ALL_REFERENCES).forEach(([fieldName, reference]) => {
    if (!reference.code) {
      return;
    }

    const refCode = reference.code.trim().toUpperCase();
    const item = resourceCodeMap.get(refCode);

    if (item) {
      const rate = item.nettRate || item.baseRate || 0;
      const description = item.resourceDescription || fieldName;
      const unit = fieldName in FIELD_UNITS ? FIELD_UNITS[fieldName] : (item.unit || '');

      rateMap.set(fieldName, { rate, description, unit, resourceCode: refCode });
    } else {
      // Item not found - use field name as fallback description
      rateMap.set(fieldName, {
        rate: 0,
        description: fieldName,
        unit: FIELD_UNITS[fieldName] ?? '',
        resourceCode: '',
      });
    }
  });

  return rateMap;
}

/**
 * Get rate info for a specific field name
 */
export function getCutFillsRateInfo(
  fieldName: string,
  rateLookupMap: Map<string, RateInfo>,
): RateInfo {
  return rateLookupMap.get(fieldName) || { rate: 0, description: fieldName, unit: FIELD_UNITS[fieldName] ?? '' };
}

/**
 * Apply rates and descriptions to Cut & Fills summary items from item library
 */
export function applyCutFillsRatesToSummary(
  summaryItems: any[],
  rateLookupMap: Map<string, RateInfo>,
): any[] {
  return summaryItems.map((item) => {
    const rateInfo = getCutFillsRateInfo(item.fieldName, rateLookupMap);
    const amount = Math.abs(item.quantity * rateInfo.rate);

    return {
      ...item,
      rate: rateInfo.rate,
      description: item.description,
      unit: rateInfo.unit,
      amount,
      resourceCode: rateInfo.resourceCode,
    };
  });
}
